import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { errorMessageKey } from '@/shared/lib/errors';
import { Async } from '@/shared/ui/Async';
import { Badge } from '@/shared/ui/Badge';
import { Spinner } from '@/shared/ui/Spinner';

import { openCredential, useMyCredentials, useUploadCredential } from '../hooks/use-credentials';
import { CREDENTIAL_MAX_BYTES, CREDENTIAL_TYPES, type CredentialStatus } from '../model/credential';

const statusColor = (status: CredentialStatus): string =>
  status === 'approved'
    ? 'var(--color-success)'
    : status === 'rejected'
      ? 'var(--color-danger)'
      : 'var(--color-accent-text)';

/** Provider uploads credential documents (ijazah scans, etc.); admin reviews them. */
export function CredentialsRoute(): JSX.Element {
  const { t } = useTranslation();
  const query = useMyCredentials();
  const upload = useUploadCredential();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  function onFile(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    if (file.size > CREDENTIAL_MAX_BYTES) {
      setError(t('credentials.tooLarge'));
      return;
    }
    if (!(CREDENTIAL_TYPES as readonly string[]).includes(file.type)) {
      setError(t('credentials.badType'));
      return;
    }
    upload.mutate(file, {
      onError: (err) => setError(t(errorMessageKey(err))),
      onSuccess: () => {
        if (inputRef.current) inputRef.current.value = '';
      },
    });
  }

  return (
    <main className="page stack">
      <h1>{t('credentials.title')}</h1>
      <p className="muted">{t('credentials.hint')}</p>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png"
        aria-label={t('credentials.upload')}
        onChange={onFile}
      />
      {upload.isPending ? <Spinner /> : null}
      {error ? (
        <p className="text-danger" role="alert">
          {error}
        </p>
      ) : null}

      <Async query={query} isEmpty={(l) => l.length === 0} emptyMessage={t('credentials.empty')}>
        {(docs) => (
          <div className="stack">
            {docs.map((d) => (
              <div key={d.id} className="card row row--between">
                <button
                  type="button"
                  className="btn btn--text"
                  onClick={() => openCredential(d.storagePath)}
                >
                  {d.filename}
                </button>
                <Badge label={t(`credentials.status_${d.status}`)} color={statusColor(d.status)} />
              </div>
            ))}
          </div>
        )}
      </Async>
    </main>
  );
}
